import React from 'react';
import { Button, Popover, Select } from 'antd';
import { PlusOutlined, MinusOutlined, UserOutlined } from '@ant-design/icons';
import { IReservationCompanyFilter } from '@entities/reservation-company-filter';
import { IReservationRoomFilter } from '@entities/reservation-room-filter';
import './guests.css';

const MAX_ADULTS = 10;
const MAX_CHILDREN = 10;

interface IGuestsQty {
    filter: IReservationCompanyFilter | IReservationRoomFilter;
    className?: string;
    form?: any;
    setFilter: (value: any) => void;
    openPopover: boolean;
    handleOpenPopoverChange: any;
    icon?: React.ReactNode;
}

const plural = (n: number, one: string, few: string, many: string) => {
    const abs = Math.abs(n) % 100;
    const last = abs % 10;
    if (abs > 10 && abs < 20) return many;
    if (last > 1 && last < 5) return few;
    if (last === 1) return one;
    return many;
};

const ageLabel = (age: number) => `${age} ${plural(age, 'год', 'года', 'лет')}`;

const ageOptions = Array.from({ length: 17 }, (_, i) => {
    const value = i + 1;
    return { value, label: ageLabel(value) };
});

const childAgesOf = (filter: IGuestsQty['filter']) => filter.childAges ?? [];

const GuestsStepper = ({
    value,
    min,
    max,
    onChange,
}: {
    value: number;
    min: number;
    max: number;
    onChange: (next: number) => void;
}) => (
    <div className='guests-stepper'>
        <button
            type='button'
            className='guests-step'
            disabled={value <= min}
            aria-label='Уменьшить'
            onClick={() => onChange(value - 1)}
        >
            <MinusOutlined />
        </button>
        <span className='guests-step-value'>{value}</span>
        <button
            type='button'
            className='guests-step'
            disabled={value >= max}
            aria-label='Увеличить'
            onClick={() => onChange(value + 1)}
        >
            <PlusOutlined />
        </button>
    </div>
);

const GuestsControl = ({ ...props }: IGuestsQty) => {
    const { className, filter, setFilter, openPopover, handleOpenPopoverChange, icon } = props;
    const childAges = childAgesOf(filter);

    const buttonLabel = () => {
        const adults = `${filter.adultsQty} ${plural(filter.adultsQty, 'взрослый', 'взрослых', 'взрослых')}`;
        if (!filter.childsQty) return adults;
        return `${adults} · ${filter.childsQty} ${plural(filter.childsQty, 'ребёнок', 'ребёнка', 'детей')}`;
    };

    const agesReady = Array.from({ length: filter.childsQty }, (_, index) => childAges[index]).every((age) => age > 0);

    const setAdults = (adultsQty: number) => setFilter({ ...filter, adultsQty });

    const setChildren = (childsQty: number) => {
        setFilter({
            ...filter,
            childsQty,
            childAges: childAges.slice(0, childsQty),
        });
    };

    const setChildAge = (index: number, value: number) => {
        const nextAges = childAges.slice();
        nextAges[index] = value;
        setFilter({ ...filter, childAges: nextAges });
    };

    const onClose = () => {
        const validAges = childAges.filter((age) => age > 0).slice(0, filter.childsQty);
        if (validAges.length !== filter.childsQty || validAges.length !== childAges.length) {
            setFilter({ ...filter, childsQty: validAges.length, childAges: validAges });
        }
        handleOpenPopoverChange(false);
    };

    const onOpen = (open: boolean) => {
        open ? handleOpenPopoverChange(true) : onClose();
    };

    return (
        <Popover
            open={openPopover}
            onOpenChange={onOpen}
            trigger='click'
            placement='bottomLeft'
            className={className}
            overlayClassName='guests-popover'
            content={
                <div className='guests-panel'>
                    <div className='guests-row'>
                        <div className='guests-row-text'>
                            <div className='guests-row-title'>Взрослые</div>
                            <div className='guests-row-hint'>От 18 лет</div>
                        </div>
                        <GuestsStepper value={filter.adultsQty} min={1} max={MAX_ADULTS} onChange={setAdults} />
                    </div>
                    <div className='guests-row'>
                        <div className='guests-row-text'>
                            <div className='guests-row-title'>Дети</div>
                            <div className='guests-row-hint'>До 17 лет</div>
                        </div>
                        <GuestsStepper value={filter.childsQty} min={0} max={MAX_CHILDREN} onChange={setChildren} />
                    </div>
                    {filter.childsQty > 0 && (
                        <div className='guests-ages'>
                            <div className='guests-ages-caption'>Возраст детей на момент поездки</div>
                            {Array.from({ length: filter.childsQty }, (_, index) => (
                                <div className='guests-age-row' key={`child-${index}`}>
                                    <div className='guests-age-label'>Ребёнок {index + 1}</div>
                                    <Select
                                        className='guests-age-select'
                                        placeholder='Возраст'
                                        options={ageOptions}
                                        value={childAges[index] || undefined}
                                        onChange={(value) => setChildAge(index, value)}
                                        getPopupContainer={(node) => (node.closest('.guests-panel') as HTMLElement) ?? document.body}
                                    />
                                </div>
                            ))}
                        </div>
                    )}
                    <Button className='guests-apply' onClick={onClose} disabled={!agesReady}>
                        {agesReady ? 'Готово' : 'Укажите возраст'}
                    </Button>
                </div>
            }
        >
            <Button className='guests-qty-container'>
                {icon ?? <UserOutlined />}
                <span className='guests-qty-label'>{buttonLabel()}</span>
            </Button>
        </Popover>
    );
};

export default GuestsControl;
